import { FetchResult, InternalRefetchQueriesInclude } from '@apollo/client';
import { AnalyticsContext } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { addError } from '@shared/utils/logger';
import { useContext } from 'react';
import {
  AutoPopulateAccommodationPlacesPayloadV4,
  AutoPopulateAccommodationPlacesV4Mutation,
  ScoringPriorityField,
  useAutoPopulateAccommodationPlacesV4Mutation,
} from '@graphql/generated';
import { parseNetworkErrorDetails } from '@shared/utils/parseNetworkErrorDetails';
import { AutoPopulatedAccommodationInsight, setAutoPopulatedAccommodationInsights } from './autoPopulatedAccommodationInsights';
import { useLocaleContext } from '@shared/core/i18n/LocaleContext';

export interface CallAutoPopulateAccommodationPlacesV4Payload
  extends Readonly<{
    eventId?: string | null;
    location: Readonly<{ latitude: number; longitude: number }>;
    scoringPriorityFields: ReadonlyArray<ScoringPriorityField>;
    context: string;
    searchRadiusKm: number;
  }> {}

type UseAutoPopulateAccommodationPlacesV4Options = Readonly<{
  /**
   * When true, insight copy from the auto-populate mutation is written to localStorage for later retrieval.
   */
  shouldPersistInsights?: boolean;
}>;

export const useAutoPopulateAccommodationPlacesV4 = (
  refetchQueries?: ((result: FetchResult) => InternalRefetchQueriesInclude) | InternalRefetchQueriesInclude,
  options: UseAutoPopulateAccommodationPlacesV4Options = {},
) => {
  const analytics = useContext(AnalyticsContext);
  const { shouldPersistInsights = false } = options;
  const { language: locale } = useLocaleContext();

  const [autoPopulateAccommodationPlacesV4, { loading: loading }] = useAutoPopulateAccommodationPlacesV4Mutation();

  const extractAutoPopulatedPlaces = useEventCallback((data: AutoPopulateAccommodationPlacesV4Mutation): ReadonlyArray<AutoPopulatedAccommodationInsight> => {
    return data.autoPopulateAccommodationPlacesV4.map(place => ({
      accommodationPlaceId: place.accommodationPlaceId,
      whyThisHotelIsAGreatPick: place.whyThisHotelIsAGreatPick ?? null,
      proposedNoteFromHostToGuest: place.proposedNoteFromHostToGuest ?? null,
    }));
  });

  const callAutoPopulateAccommodationPlacesV4 = useEventCallback(
    async (payloadArgs: CallAutoPopulateAccommodationPlacesV4Payload): Promise<ReadonlyArray<AutoPopulatedAccommodationInsight>> => {
      const { eventId, location, scoringPriorityFields, context, searchRadiusKm } = payloadArgs;

      const payload: AutoPopulateAccommodationPlacesPayloadV4 = {
        eventId: eventId!,
        force: true,
        location,
        searchRadiusKm,
        locale,
        scoringPriorityFields: [...scoringPriorityFields],
      };

      if (!eventId) {
        const error = new Error('Missing event id');
        addError(error, {
          context,
          eventId: payload.eventId,
          latitude: payload.location?.latitude,
          longitude: payload.location?.longitude,
          version: 4,
        });
        throw error;
      }

      try {
        const response = await autoPopulateAccommodationPlacesV4({ variables: { payload }, refetchQueries });

        if (response.errors && response.errors.length > 0) {
          const error = Array.isArray(response.errors) ? response.errors[0] : (response.errors as unknown as Error);
          throw error;
        }

        if (!response.data) {
          return [];
        }

        const autoPopulatedPlaces = extractAutoPopulatedPlaces(response.data);
        if (shouldPersistInsights && payload.eventId) {
          setAutoPopulatedAccommodationInsights(payload.eventId, autoPopulatedPlaces);
        }

        analytics.track({
          category: 'accommodations',
          action: `TravelButtonInteracted`,
          label: 'autoPopulateAccommodationPlaces',
          extraInfo: {
            name: `autoPopulateAccommodationPlaces`,
            eventId: payload.eventId,
            latitude: payload.location?.latitude,
            longitude: payload.location?.longitude,
            placesCount: autoPopulatedPlaces.length,
            version: 4,
            context,
          },
        });

        return autoPopulatedPlaces;
      } catch (error) {
        const { errorExplanation, statusCode, isTimeout } = parseNetworkErrorDetails(error);
        addError(errorExplanation, {
          context,
          eventId: payload.eventId,
          latitude: payload.location?.latitude,
          longitude: payload.location?.longitude,
          version: 4,
          statusCode,
          isTimeout,
          originalError: error,
        });
        throw error;
      }
    },
  );

  return { callAutoPopulateAccommodationPlacesV4, loading };
};
